import { useState } from "react";
import { Layers, CreditCard, BookOpen, X } from "lucide-react";

const STORAGE_KEY = "quran_view_picker_done";
const DEFAULT_MODE_KEY = "quran_default_view_mode";

const MODES = [
  {
    id: "mushaf",
    label: "Surah Mode",
    Icon: Layers,
    blurb:
      "Continuous flowing text of an entire surah. Best for steady reading and review sessions.",
  },
  {
    id: "flashcard",
    label: "Flashcard Mode",
    Icon: CreditCard,
    blurb:
      "One ayah at a time with a tap-to-reveal translation. Best for active memorization & spaced repetition.",
  },
  {
    id: "page",
    label: "Mushaf Page Mode",
    Icon: BookOpen,
    blurb:
      "Authentic 604-page Madinah Mushaf layout, two-page spread on desktop. Best for hifz visualisation by page.",
  },
];

export function shouldShowViewModePicker() {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) !== "1";
}

export function markViewModePickerSeen() {
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, "1");
  }
}

export function getDefaultViewMode() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(DEFAULT_MODE_KEY);
}

export function saveDefaultViewMode(mode) {
  if (typeof window !== "undefined") {
    localStorage.setItem(DEFAULT_MODE_KEY, mode);
  }
}

export default function ViewModePicker({ open, onPick, onClose }) {
  const [hovered, setHovered] = useState(null);
  if (!open) return null;

  const skip = () => {
    markViewModePickerSeen();
    onClose();
  };

  const choose = (mode) => {
    saveDefaultViewMode(mode);
    markViewModePickerSeen();
    onPick(mode);
  };

  return (
    <div
      data-testid="view-mode-picker"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="relative w-full max-w-2xl rounded-2xl bg-[#0D2818] border border-[#C9A227]/40 shadow-2xl p-6 sm:p-8 text-[#E8D5A3]">
        <button
          data-testid="view-mode-picker-close-btn"
          onClick={skip}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-2xl font-cinzel mb-1">Pick your reading style</h3>
        <p className="text-sm font-outfit text-[#E8D5A3]/70 mb-6">
          We'll open the Reader in this mode by default. You can switch any time from the header.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {MODES.map((m) => {
            const Icon = m.Icon;
            const isHover = hovered === m.id;
            return (
              <button
                key={m.id}
                data-testid={`view-mode-pick-${m.id}`}
                onClick={() => choose(m.id)}
                onMouseEnter={() => setHovered(m.id)}
                onMouseLeave={() => setHovered(null)}
                className={`group relative text-left p-5 rounded-xl border transition-all ${
                  isHover
                    ? "border-[#C9A227] bg-[#C9A227]/10 -translate-y-0.5"
                    : "border-[#C9A227]/25 bg-[#FBF6E9]/[0.03]"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 transition-colors ${
                    isHover ? "bg-[#C9A227] text-[#0D2818]" : "bg-[#C9A227]/15 text-[#C9A227]"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h4 className="font-cinzel text-base mb-1.5">{m.label}</h4>
                <p className="text-xs font-outfit text-[#E8D5A3]/70 leading-relaxed">
                  {m.blurb}
                </p>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-end">
          <button
            data-testid="view-mode-picker-skip-btn"
            onClick={skip}
            className="text-xs text-[#8B9D83] hover:text-[#E8D5A3] transition-colors font-outfit"
          >
            Decide later
          </button>
        </div>
      </div>
    </div>
  );
}
